/**
 * React's view-transition timeout (release 14). When a DOM update outlasts the browser's limit,
 * the browser skips the animation and rejects the transition's promise, which React's
 * <ViewTransition> leaves unhandled: the page then reports `Transition was aborted because of
 * timeout in DOM update` as an error (CI's phone e2e, release 13; a slow real phone too), though
 * nothing went wrong but the animation. One predicate names that rejection: the app's entry
 * prevents it (`pasangPenahanTransisi`, ../main.tsx), and the Sentry setup drops it (../sentry,
 * and the launch queue of ./pelaporan). Every other rejection passes untouched.
 */

/** A view transition's own words: aborted or skipped. */
const PESAN_TRANSISI = /transition was (aborted|skipped)|view transition/i
/**
 * An InvalidStateError only for a transition skipped because the page was hidden; the same name
 * also reports "Transition was aborted because of invalid state", a duplicate
 * view-transition-name, which is a styling bug and stays reported (release 14 review M-3).
 */
const PESAN_TERSEMBUNYI = /skipp(?:ed|ing) .*hidden/i

/** A DOMException of a view transition the browser aborted or skipped; nothing else. */
export function adalahBatasTransisi(alasan: unknown): boolean {
  if (!(alasan instanceof DOMException)) return false
  if (alasan.name === 'TimeoutError' || alasan.name === 'AbortError') {
    return PESAN_TRANSISI.test(alasan.message)
  }
  return (
    alasan.name === 'InvalidStateError' &&
    PESAN_TRANSISI.test(alasan.message) &&
    PESAN_TERSEMBUNYI.test(alasan.message)
  )
}

/** What the listener needs of an `unhandledrejection` (the node tests' programs have no DOM lib). */
interface Tolakan {
  readonly reason: unknown
  preventDefault(): void
}

/** The window, as far as the listener goes. */
interface SasaranTolakan {
  addEventListener(type: 'unhandledrejection', dengar: (e: Tolakan) => void): void
  removeEventListener(type: 'unhandledrejection', dengar: (e: Tolakan) => void): void
}

/** The one `unhandledrejection` listener: prevents only that rejection. Returns its removal. */
export function pasangPenahanTransisi(win: SasaranTolakan): () => void {
  const dengar = (e: Tolakan) => {
    if (adalahBatasTransisi(e.reason)) e.preventDefault()
  }
  win.addEventListener('unhandledrejection', dengar)
  return () => win.removeEventListener('unhandledrejection', dengar)
}
